import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaUndo } from 'react-icons/fa';
import { LuVideo } from 'react-icons/lu';
import { useToast } from '../../../context/ToastContext';
import PageLayout from '../../../components/ui/PageLayout';
import PageHeader from '../../../components/ui/PageHeader';
import SkeletonLoader from '../../../components/ui/SkeletonLoader';
import PortalIconPicker from '../../../components/PortalCMS/PortalIconPicker';

const SLUG = 'meetings';

const defaultForm = () => ({
  isPublished: true,
  pageTitle: '',
  pageSubtitle: '',
  pageIcon: 'LuVideo',
  scheduleButtonLabel: '',
  loadingMessage: '',
  upcomingTabLabel: '',
  ongoingTabLabel: '',
  pastTabLabel: '',
  emptyUpcomingTitle: '',
  emptyOngoingTitle: '',
  emptyPastTitle: '',
  emptyScheduleButtonLabel: '',
  joinMeetingLabel: '',
  durationSuffix: '',
  modalTitle: '',
  modalSubtitle: '',
  titleFieldLabel: '',
  titlePlaceholder: '',
  descriptionFieldLabel: '',
  descriptionPlaceholder: '',
  datetimeFieldLabel: '',
  datetimeHint: '',
  durationNote: '',
  istEquivalentLabel: '',
  cancelButtonLabel: '',
  createButtonLabel: '',
  creatingButtonLabel: '',
  weekdayError: '',
  timeWindowError: '',
  createSuccessMessage: '',
  createErrorMessage: '',
});

const MeetingsPortalCMS = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [form, setForm] = useState(defaultForm());

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setForm({ ...defaultForm(), ...(res.data.data.content || {}), isPublished: res.data.data.isPublished !== false });
      }
    } catch {
      toastError('Failed to load meetings page content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`,
        { content: form, isPublished: form.isPublished },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) toastSuccess('Meetings page saved');
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore preloaded meetings page defaults?')) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Defaults restored');
      fetchContent();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <SkeletonLoader count={4} />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title="Meetings Page"
        description="Edit the User Portal /meetings page labels and text."
        icon={LuVideo}
        actions={
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => navigate('/portal-cms')} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 text-sm">Back</button>
            <button type="button" onClick={handleReset} disabled={resetting} className="px-4 py-2 rounded-xl border text-sm flex items-center gap-2">
              <FaUndo className="w-3.5 h-3.5" /> Reset
            </button>
            <button type="button" onClick={handleSave} disabled={saving} className="btn-premium text-sm py-2 px-4 flex items-center gap-2">
              <FaSave className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      />

      <div className="bg-white dark:bg-dark-800 rounded-2xl border p-6 space-y-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.isPublished !== false} onChange={(e) => updateField('isPublished', e.target.checked)} />
          Published
        </label>

        <h3 className="font-bold pt-2">Page header</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="input-modern" value={form.pageTitle} onChange={(e) => updateField('pageTitle', e.target.value)} placeholder="Page title" />
          <PortalIconPicker value={form.pageIcon} onChange={(icon) => updateField('pageIcon', icon)} />
        </div>
        <textarea className="input-modern min-h-[70px]" value={form.pageSubtitle} onChange={(e) => updateField('pageSubtitle', e.target.value)} placeholder="Page subtitle" />
        <input className="input-modern" value={form.scheduleButtonLabel} onChange={(e) => updateField('scheduleButtonLabel', e.target.value)} placeholder="Schedule button label" />
        <input className="input-modern" value={form.loadingMessage} onChange={(e) => updateField('loadingMessage', e.target.value)} placeholder="Loading message" />

        <h3 className="font-bold pt-2">Tabs</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input className="input-modern" value={form.upcomingTabLabel} onChange={(e) => updateField('upcomingTabLabel', e.target.value)} placeholder="Upcoming tab" />
          <input className="input-modern" value={form.ongoingTabLabel} onChange={(e) => updateField('ongoingTabLabel', e.target.value)} placeholder="Ongoing tab" />
          <input className="input-modern" value={form.pastTabLabel} onChange={(e) => updateField('pastTabLabel', e.target.value)} placeholder="Past tab" />
        </div>

        <h3 className="font-bold pt-2">Empty states</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="input-modern" value={form.emptyUpcomingTitle} onChange={(e) => updateField('emptyUpcomingTitle', e.target.value)} placeholder="Empty upcoming title" />
          <input className="input-modern" value={form.emptyOngoingTitle} onChange={(e) => updateField('emptyOngoingTitle', e.target.value)} placeholder="Empty ongoing title" />
          <input className="input-modern" value={form.emptyPastTitle} onChange={(e) => updateField('emptyPastTitle', e.target.value)} placeholder="Empty past title" />
          <input className="input-modern" value={form.emptyScheduleButtonLabel} onChange={(e) => updateField('emptyScheduleButtonLabel', e.target.value)} placeholder="Empty state schedule button" />
        </div>

        <h3 className="font-bold pt-2">Meeting cards</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="input-modern" value={form.joinMeetingLabel} onChange={(e) => updateField('joinMeetingLabel', e.target.value)} placeholder="Join meeting button" />
          <input className="input-modern" value={form.durationSuffix} onChange={(e) => updateField('durationSuffix', e.target.value)} placeholder="Duration suffix (e.g. mins)" />
        </div>

        <h3 className="font-bold pt-2">Schedule modal</h3>
        <input className="input-modern" value={form.modalTitle} onChange={(e) => updateField('modalTitle', e.target.value)} placeholder="Modal title" />
        <textarea className="input-modern min-h-[70px]" value={form.modalSubtitle} onChange={(e) => updateField('modalSubtitle', e.target.value)} placeholder="Modal subtitle" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="input-modern" value={form.titleFieldLabel} onChange={(e) => updateField('titleFieldLabel', e.target.value)} placeholder="Title field label" />
          <input className="input-modern" value={form.titlePlaceholder} onChange={(e) => updateField('titlePlaceholder', e.target.value)} placeholder="Title placeholder" />
          <input className="input-modern" value={form.descriptionFieldLabel} onChange={(e) => updateField('descriptionFieldLabel', e.target.value)} placeholder="Description field label" />
          <input className="input-modern" value={form.descriptionPlaceholder} onChange={(e) => updateField('descriptionPlaceholder', e.target.value)} placeholder="Description placeholder" />
          <input className="input-modern" value={form.datetimeFieldLabel} onChange={(e) => updateField('datetimeFieldLabel', e.target.value)} placeholder="Date/time field label" />
          <input className="input-modern" value={form.datetimeHint} onChange={(e) => updateField('datetimeHint', e.target.value)} placeholder="Date/time hint" />
          <input className="input-modern" value={form.durationNote} onChange={(e) => updateField('durationNote', e.target.value)} placeholder="Duration note" />
          <input className="input-modern" value={form.istEquivalentLabel} onChange={(e) => updateField('istEquivalentLabel', e.target.value)} placeholder="IST equivalent label" />
          <input className="input-modern" value={form.cancelButtonLabel} onChange={(e) => updateField('cancelButtonLabel', e.target.value)} placeholder="Cancel button" />
          <input className="input-modern" value={form.createButtonLabel} onChange={(e) => updateField('createButtonLabel', e.target.value)} placeholder="Create button" />
          <input className="input-modern" value={form.creatingButtonLabel} onChange={(e) => updateField('creatingButtonLabel', e.target.value)} placeholder="Creating button" />
        </div>

        <h3 className="font-bold pt-2">Validation & messages</h3>
        <textarea className="input-modern min-h-[60px]" value={form.weekdayError} onChange={(e) => updateField('weekdayError', e.target.value)} placeholder="Weekday validation error" />
        <textarea className="input-modern min-h-[60px]" value={form.timeWindowError} onChange={(e) => updateField('timeWindowError', e.target.value)} placeholder="Time window validation error" />
        <input className="input-modern" value={form.createSuccessMessage} onChange={(e) => updateField('createSuccessMessage', e.target.value)} placeholder="Create success toast" />
        <input className="input-modern" value={form.createErrorMessage} onChange={(e) => updateField('createErrorMessage', e.target.value)} placeholder="Create error toast" />
      </div>
    </PageLayout>
  );
};

export default MeetingsPortalCMS;
